/**
 * FarmAnalytics.tsx
 * Displays comprehensive analytics and metrics for a farm, including financial data,
 * production statistics, resource utilization, and workforce analytics.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  useTheme,
  Divider,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DetailLayout from './layout/DetailLayout';
import { supabase } from '../../../lib/supabase';
import AnalyticsDashboard from '../../workers/AnalyticsDashboard';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import PetsIcon from '@mui/icons-material/Pets';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface FarmAnalyticsProps {
  farm: any;
}

// --- Styled Components ---

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px 0 rgba(0,0,0,0.1)',
  },
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
}));

// --- Helper Components ---

interface SummaryCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  trend?: { value: number; label: string };
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, subtitle, icon, color, trend }) => {
  const theme = useTheme();
  
  return (
    <StyledCard>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <MetricValue color="text.primary">
              {value}
            </MetricValue>
          </Box>
          <IconWrapper sx={{ bgcolor: `${color}.light`, color: `${color}.main` }}>
            {icon}
          </IconWrapper>
        </Box>
        
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}

        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            {trend.value >= 0 ? (
              <TrendingUpIcon color="success" fontSize="small" />
            ) : (
              <TrendingDownIcon color="error" fontSize="small" />
            )}
            <Typography
              variant="body2"
              color={trend.value >= 0 ? 'success.main' : 'error.main'}
              sx={{ ml: 0.5, fontWeight: 600 }}
            >
              {Math.abs(trend.value)}%
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {trend.label}
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

// --- Main Component ---

const FarmAnalytics: React.FC<FarmAnalyticsProps> = ({ farm }) => {
  const theme = useTheme();
  const farmId = farm?.id;

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data State
  const [financials, setFinancials] = useState({
    income: 0,
    expenses: 0,
    netProfit: 0,
    monthlyData: { labels: [], income: [], expenses: [] } as any
  });
  
  const [production, setProduction] = useState({
    activeCrops: 0,
    totalArea: 0,
    harvestedCrops: 0,
    livestockCount: 0,
    sickAnimals: 0,
    cropDistribution: { labels: [], data: [] } as any,
    animalDistribution: { labels: [], data: [] } as any
  });

  const [workforce, setWorkforce] = useState({
    totalWorkers: 0,
    presentToday: 0
  });

  useEffect(() => {
    if (farmId) {
      fetchAllData();
    }
  }, [farmId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Fetch Financials
      const { data: financeData } = await supabase
        .from('finances')
        .select('*')
        .eq('status', 'completed')
        .order('date', { ascending: true });

      const income = financeData?.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0) || 0;
      const expenses = financeData?.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0) || 0;

      // Process monthly data for chart
      const months: Record<string, { income: number; expenses: number }> = {};
      financeData?.forEach(t => {
        const month = new Date(t.date).toLocaleString('default', { month: 'short' });
        if (!months[month]) months[month] = { income: 0, expenses: 0 };
        if (t.type === 'income') months[month].income += t.amount;
        else months[month].expenses += t.amount;
      });

      const monthLabels = Object.keys(months);
      
      // 2. Fetch Crops
      const { data: cropsData } = await supabase
        .from('farm_crops')
        .select('*')
        .eq('farm_id', farmId);

      const activeCrops = cropsData?.filter(c => c.status !== 'harvested' && c.status !== 'failed').length || 0;
      const totalArea = cropsData?.reduce((sum, c) => sum + (c.area || 0), 0) || 0;
      const harvestedCrops = cropsData?.filter(c => c.status === 'harvested').length || 0;

      // Crop distribution
      const cropTypes: Record<string, number> = {};
      cropsData?.forEach(c => {
        cropTypes[c.name] = (cropTypes[c.name] || 0) + 1;
      });

      // 3. Fetch Livestock
      const { data: animalsData } = await supabase
        .from('animals')
        .select('*')
        .eq('farm_id', farmId);

      let livestockCount = animalsData?.length || 0;
      const sickAnimals = (animalsData || []).filter((a: any) => (a.status || '').toString().toLowerCase() === 'sick').length;
      const animalTypes: Record<string, number> = {};

      (animalsData || []).forEach((a: any) => {
        const type = (a.type || 'Other').toString();
        animalTypes[type] = (animalTypes[type] || 0) + 1;
      });

      // 4. Fetch Workers Summary (Basic count)
      const { count: workerCount } = await supabase
        .from('workers')
        .select('*', { count: 'exact', head: true })
        .eq('farm_id', farmId);

      setFinancials({
        income,
        expenses,
        netProfit: income - expenses,
        monthlyData: {
          labels: monthLabels,
          income: monthLabels.map(m => months[m].income),
          expenses: monthLabels.map(m => months[m].expenses)
        }
      });

      setProduction({
        activeCrops,
        totalArea,
        harvestedCrops,
        livestockCount,
        sickAnimals, // Placeholder
        cropDistribution: {
          labels: Object.keys(cropTypes),
          data: Object.values(cropTypes)
        },
        animalDistribution: {
          labels: Object.keys(animalTypes),
          data: Object.values(animalTypes)
        }
      });

      setWorkforce({
        totalWorkers: workerCount || 0,
        presentToday: 0 // Would need attendance query, leaving as 0 or fetching if critical
      });

    } catch (err: any) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load some analytics data. ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!farm) return <Typography>Loading farm data...</Typography>;

  return (
    <DetailLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Farm Analytics
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Comprehensive view of your farm's performance and health
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="analytics tabs">
          <Tab label="Overview" />
          <Tab label="Financials" />
          <Tab label="Production" />
          <Tab label="Workforce" />
        </Tabs>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {/* OVERVIEW TAB */}
          {tabValue === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  title="Total Revenue"
                  value={`KES ${financials.income.toLocaleString()}`}
                  icon={<AttachMoneyIcon />}
                  color="success"
                  trend={{ value: 12, label: 'vs last month' }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  title="Total Expenses"
                  value={`KES ${financials.expenses.toLocaleString()}`}
                  icon={<TrendingDownIcon />}
                  color="error"
                  subtitle="Includes labor & inputs"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  title="Active Crops"
                  value={production.activeCrops}
                  icon={<AgricultureIcon />}
                  color="primary"
                  subtitle={`${production.totalArea} acres cultivated`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  title="Livestock"
                  value={production.livestockCount}
                  icon={<PetsIcon />}
                  color="warning"
                  subtitle="Total heads"
                />
              </Grid>

              <Grid item xs={12} md={8}>
                <Card sx={{ p: 2, height: '100%' }}>
                    <Typography variant="h6" gutterBottom>Revenue vs Expenses</Typography>
                    <Box sx={{ height: 300 }}>
                        <Bar
                            data={{
                                labels: financials.monthlyData.labels.length ? financials.monthlyData.labels : ['No Data'],
                                datasets: [
                                    {
                                        label: 'Income',
                                        data: financials.monthlyData.income.length ? financials.monthlyData.income : [0],
                                        backgroundColor: theme.palette.success.main,
                                        borderRadius: 4,
                                    },
                                    {
                                        label: 'Expenses',
                                        data: financials.monthlyData.expenses.length ? financials.monthlyData.expenses : [0],
                                        backgroundColor: theme.palette.error.main,
                                        borderRadius: 4,
                                    }
                                ]
                            }}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: { legend: { position: 'top' } }
                            }}
                        />
                    </Box>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                  <Card sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" gutterBottom>Production Mix</Typography>
                      <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                          <Doughnut
                              data={{
                                  labels: production.cropDistribution.labels.length ? production.cropDistribution.labels : ['No Crops'],
                                  datasets: [{
                                      data: production.cropDistribution.data.length ? production.cropDistribution.data : [1],
                                      backgroundColor: [
                                          theme.palette.primary.main,
                                          theme.palette.secondary.main,
                                          theme.palette.warning.main,
                                          theme.palette.info.main,
                                          theme.palette.success.main,
                                      ]
                                  }]
                              }}
                              options={{
                                  maintainAspectRatio: false,
                                  plugins: { legend: { position: 'bottom' } }
                              }}
                          />
                      </Box>
                  </Card>
              </Grid>
            </Grid>
          )}

          {/* FINANCIALS TAB */}
          {tabValue === 1 && (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Detailed financial breakdown and transaction history.
                    </Alert>
                </Grid>
                {/* Reusing chart for now, but could be more detailed */}
                <Grid item xs={12} md={8}>
                    <Card sx={{ p: 3 }}>
                        <Typography variant="h6">Monthly Financial Performance</Typography>
                        <Box sx={{ height: 400, mt: 2 }}>
                            <Line
                                data={{
                                    labels: financials.monthlyData.labels,
                                    datasets: [
                                        {
                                            label: 'Net Profit',
                                            data: financials.monthlyData.labels.map((_: any, i: number) => 
                                                financials.monthlyData.income[i] - financials.monthlyData.expenses[i]
                                            ),
                                            borderColor: theme.palette.primary.main,
                                            tension: 0.4,
                                            fill: true,
                                            backgroundColor: `${theme.palette.primary.main}20`
                                        }
                                    ]
                                }}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false
                                }}
                            />
                        </Box>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>Key Financial Stats</Typography>
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" color="text.secondary">Total Income</Typography>
                                <Typography variant="h5" color="success.main">KES {financials.income.toLocaleString()}</Typography>
                            </Box>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" color="text.secondary">Total Expenses</Typography>
                                <Typography variant="h5" color="error.main">KES {financials.expenses.toLocaleString()}</Typography>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            <Box>
                                <Typography variant="body2" color="text.secondary">Net Profit</Typography>
                                <Typography variant="h4" color="primary.main">KES {financials.netProfit.toLocaleString()}</Typography>
                            </Box>
                        </Box>
                    </Card>
                </Grid>
            </Grid>
          )}

          {/* PRODUCTION TAB */}
          {tabValue === 2 && (
             <Grid container spacing={3}>
                 <Grid item xs={12} md={6}>
                     <Card sx={{ p: 3 }}>
                         <Typography variant="h6" gutterBottom>Crop Distribution</Typography>
                         <Box sx={{ height: 300 }}>
                            <Doughnut
                                data={{
                                    labels: production.cropDistribution.labels,
                                    datasets: [{
                                        data: production.cropDistribution.data,
                                        backgroundColor: [
                                            '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'
                                        ]
                                    }]
                                }}
                                options={{ maintainAspectRatio: false }}
                            />
                         </Box>
                     </Card>
                 </Grid>
                 <Grid item xs={12} md={6}>
                     <Card sx={{ p: 3 }}>
                         <Typography variant="h6" gutterBottom>Livestock Distribution</Typography>
                         <Box sx={{ height: 300 }}>
                            <Doughnut
                                data={{
                                    labels: production.animalDistribution.labels,
                                    datasets: [{
                                        data: production.animalDistribution.data,
                                        backgroundColor: [
                                            '#FF9F40', '#FF6384', '#4BC0C0', '#FFCD56', '#36A2EB'
                                        ]
                                    }]
                                }}
                                options={{ maintainAspectRatio: false }}
                            />
                         </Box>
                     </Card>
                 </Grid>
             </Grid>
          )}

          {/* WORKFORCE TAB */}
          {tabValue === 3 && (
            <Box>
              <AnalyticsDashboard farmId={farmId} />
            </Box>
          )}
        </>
      )}
    </DetailLayout>
  );
};

export default FarmAnalytics;
